export const oohData = {
  headlineVerdict: 'Premium branding, risky in fast traffic',
  methodology: {
    name: 'Attention Blueprint',
    summary:
      'Our methodology is built on 17 visual drivers from cognitive psychology that determine what wins that first half-second of attention. Every principle is substantiated with peer-reviewed research.',
    source: 'University of Utrecht · Cognitive Psychology Research',
    stats: ['17 drivers', '47+ peer-reviewed sources'],
    // This report is observational: it predicts attention from the Blueprint.
    observationNote:
      'This report is an observational analysis — a research-backed prediction of where attention will land before a single euro of media is spent.',
  },
  attentionTesting: {
    eyebrow: 'Recommended next step',
    heading: 'Pressure-test this creative on real human behaviour',
    body: 'The Attention Blueprint predicts what should win the first half-second. Attention Testing validates it — putting your creative (and alternative variants) in front of real people to measure how they actually behave, so you ship the version proven to win attention.',
    bullets: [
      'Compare multiple variants head-to-head (A / B / C / D…)',
      'Measure real viewer behaviour, not just prediction',
      'Confirm the fixes in this report actually move the needle',
    ],
    ctaLabel: 'Explore Attention Testing',
    ctaHref: 'https://brandpulse-analytics.com/attention-testing',
  },
  driverDefinitions: {
    SIZE: 'How large and visually dominant key elements are — bigger elements get seen first, especially in a fleeting glance.',
    COLOUR:
      'Whether standout hues are present — vivid, saturated colour draws the eye pre-attentively before conscious reading begins.',
    'COLOUR CONTRAST':
      'The difference in hue between an element and its surroundings — high hue contrast makes elements pop from the background.',
    'LUMINANCE CONTRAST':
      'The brightness difference between an element and its background — the single strongest low-level driver of where the eye lands.',
    'TEXTURE CONTRAST':
      'How much surface detail differs across the frame — busy vs. smooth areas guide fixations toward the focal point.',
    'GENERAL CONTRAST':
      'Overall figure-ground separation — how cleanly the subject stands apart from everything behind it.',
    'ORIENTATION & EDGES':
      'The angles and edge structure of elements — strong, clean edges anchor attention; clashing edges disrupt legibility.',
    'CURVATURE CONTRAST':
      'Whether curved shapes stand out against straight ones — curvature can create pre-attentive pop-out.',
    FACES:
      'Presence of human faces — the brain has dedicated circuitry that captures attention on faces almost instantly.',
    'EYE GAZE':
      'Whether depicted subjects make or direct eye contact — gaze powerfully steers a viewer’s own attention.',
    'EMOTIONAL EXPRESSIONS':
      'Visible emotion in the creative — emotional cues are processed fast and boost memorability.',
    'CUTENESS / KINDCHENSCHEMA':
      'Baby-schema features (infants, young animals) — these trigger an automatic, hard-wired attention response.',
    'SPATIAL PROXIMITY / DEPTH':
      'How near the subject feels to the viewer — objects in the near-space read as more present and premium.',
    'GESTALT PRINCIPLES':
      'How the brain groups and completes shapes — good grouping aids instant comprehension; broken grouping adds cognitive load.',
    TEXT: 'Legibility and prominence of the message — text must be readable within the minimum viable exposure window.',
  } as Record<string, string>,
  brandContext: {
    brandName: 'Água de Luso',
    brandWebsite: 'https://www.aguadeluso.pt/pt/',
    industry: 'FMCG / Beverages',
    targetAudience:
      'General public, health-conscious consumers, individuals seeking natural hydration',
    brandTone: 'Natural, pure, balanced, serene, historical',
    marketSegment: 'Large National Brand',
    creativeSummary:
      "Glass bottle of Água de Luso and a branded drinking glass resting on a mossy surface in a sunlit forest setting. Large white serif text reading 'Naturalmente Equilibrada' spans the center, partially obscured by the bottle and glass.",
    oohObjective: 'Branding',
    objectiveRationale:
      'The creative is entirely focused on brand building, product purity, and natural origins. No direct call to action, QR code, promotional offer, or conversion mechanism present.',
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/do9hxtQbnJMwAsBYtxynrJ/5b9992d5-0699-4975-a16c-d1288f8c9076.jpg',
  },
  viewingContext: {
    description:
      'Landscape format, likely deployed across large-format billboards or transit shelters in urban environments. Viewing mode spans from fast (drivers and cyclists at 2–6 seconds) to medium (pedestrians at 5–10 seconds). Minimum viable exposure threshold approximately 2–3 seconds.',
    modes: [
      { label: 'Fast', time: '2–6s' },
      { label: 'Medium', time: '5–10s' },
      { label: 'Dwell', time: '10s+' },
    ],
  },
  drivers: [
    {
      id: '01',
      name: 'SIZE',
      verdict: 'strong_with_improvement',
      observation:
        "The headline 'Naturalmente Equilibrada' is massive, dominating horizontal space, and the product bottle is large and centrally placed. However, the primary Luso logo in the top left is relatively small compared to the visual weight of the headline.",
      improvement:
        'Increase the scale of the top-left Luso logo by 30% to ensure faster brand linkage during brief 2-second exposures.',
      category: 'Visual Capture',
    },
    {
      id: '02',
      name: 'COLOUR',
      verdict: 'neutral_with_improvement',
      observation:
        "Palette dominated by natural desaturated greens and earthy browns. The blue cap and red 'LUSO' on the bottle label provide the only chromatic pop, but are relatively small.",
      improvement:
        'Enhance saturation of the blue bottle cap and red logo on the label to create stronger bottom-up visual capture.',
      category: 'Visual Capture',
    },
    {
      id: '03',
      name: 'COLOUR CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        "Strong hue contrast between stark white typography and dark green/brown forest background. Red 'LUSO' text on bottle contrasts effectively with surrounding cool tones.",
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '04',
      name: 'LUMINANCE CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'Bright white text and specular highlights on the glass bottle create extreme luminance contrast against the deeply shadowed forest background, pulling the eye to the center.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '05',
      name: 'TEXTURE CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'High-frequency mossy textures and bottle reflections contrast sharply with the smooth blurred background, guiding fixations to the product.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '06',
      name: 'GENERAL CONTRAST',
      verdict: 'strong_no_improvement',
      observation:
        'Shallow depth of field isolates the bottle, glass, and text from the blurred background — excellent figure-ground separation.',
      improvement: null,
      category: 'Visual Capture',
    },
    {
      id: '07',
      name: 'ORIENTATION & EDGES',
      verdict: 'neutral_with_improvement',
      observation:
        'Strong vertical bottle edges intersect horizontal typography, creating a central cross-axis. However, text overlapping the bottle creates intersecting edges that disrupt legibility.',
      improvement:
        'Adjust kerning or placement so vertical edges of the bottle do not intersect directly through the middle of letters.',
      category: 'Shape & Edge',
    },
    {
      id: '08',
      name: 'CURVATURE CONTRAST',
      verdict: 'neutral_not_applicable',
      observation:
        'Curved bottle and rounded serif typography stand out against the rectilinear frame, but no highly disruptive curvature elements are deployed specifically for pre-attentive pop-out.',
      improvement: null,
      category: 'Shape & Edge',
    },
    {
      id: '09',
      name: 'FACES',
      verdict: 'neutral_not_applicable',
      observation: 'No human faces present in the execution.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '10',
      name: 'EYE GAZE',
      verdict: 'neutral_not_applicable',
      observation: 'No subjects with eyes depicted.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '11',
      name: 'EMOTIONAL EXPRESSIONS',
      verdict: 'neutral_not_applicable',
      observation: 'No emotional expressions depicted.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '12',
      name: 'CUTENESS / KINDCHENSCHEMA',
      verdict: 'neutral_not_applicable',
      observation:
        'No infants, young animals, or baby-schema features present.',
      improvement: null,
      category: 'Biological Attention',
    },
    {
      id: '13',
      name: 'SPATIAL PROXIMITY / DEPTH',
      verdict: 'strong_no_improvement',
      observation:
        "Shallow depth of field pushes the bottle into the viewer's near-space while the blurred forest recedes — strong sense of physical proximity to the product.",
      improvement: null,
      category: 'Structural & Cognitive',
    },
    {
      id: '14',
      name: 'GESTALT PRINCIPLES',
      verdict: 'weak_with_improvement',
      observation:
        "The bottle obscures parts of 'Naturalmente' and 'Equilibrada'. While visually sophisticated, this forces the brain to work harder to complete the words — risky for fast-moving OOH contexts.",
      improvement:
        'Bring the text layer entirely to the foreground or shift the bottle slightly off-center to eliminate reliance on cognitive closure.',
      category: 'Structural & Cognitive',
    },
    {
      id: '15',
      name: 'TEXT',
      verdict: 'weak_with_improvement',
      observation:
        'Serif typography is elegant and large, but legibility is severely compromised by being placed behind the refractive glass bottle. Message becomes visually fragmented in a fast-viewing context.',
      improvement:
        'Move the text layer in front of the product imagery to guarantee instant legibility within the 2-second minimum viable exposure threshold.',
      category: 'Structural & Cognitive',
    },
  ],
  systemDynamics: {
    // The interaction narrative — how the individual drivers combine. Shown as
    // the section intro so the risk cards below have context.
    interactionSummary:
      'Individual attention drivers don’t work in isolation. The visual hierarchy pulls the eye straight to the centre of the canvas — extreme luminance contrast, shallow depth of field and spatial proximity all reinforce the product. The catch is that the massive headline and the refractive bottle collide in that same centre, so the eye has to untangle text from product rather than absorbing them in one clean sweep. This is where a strength in one area quietly creates a risk in another.',
    risks: [
      {
        label: 'Brand Linkage Risk',
        definition: 'Will people connect what they see to your brand?',
        level: 'moderate' as const,
        reason:
          'The Luso logo is small and sits apart from the bright central cluster, so the eye locks onto “beautiful water in a forest” before it registers whose ad it is.',
      },
      {
        label: 'Attention Hierarchy Risk',
        definition: 'The order the eye travels through the creative.',
        level: 'high' as const,
        reason:
          'The headline and the refractive bottle collide in the centre, forcing the eye to untangle text from product instead of absorbing them in one clean sweep.',
      },
      {
        label: 'Context Vulnerability',
        definition:
          'How well the message survives real-world viewing conditions.',
        level: 'high' as const,
        reason:
          'In fast traffic (cars, cyclists), the reliance on the brain “completing” broken words means a viewer sees the scene but never actually reads the message.',
      },
    ],
  },
  overallAssessment:
    'This execution is a visually stunning, premium piece of branding that excels in creating a mood of natural purity through masterful use of depth, luminance contrast, and figure-ground separation. Its primary vulnerability lies in its OOH mechanics: placing the core message behind a refractive glass bottle creates a legibility barrier that is highly risky for fast-moving urban environments. While beautiful in a dwell-time setting, the cognitive load required to read the text through the bottle risks message failure in a 2-second exposure. The immediate priority should be bringing the text layer to the foreground to ensure instant comprehension without sacrificing the premium aesthetic.',
  topStrengths: [
    {
      name: 'LUMINANCE CONTRAST',
      description:
        'Extreme contrast between bright white text and dark forest background instantly anchors attention to the center of the canvas.',
    },
    {
      name: 'GENERAL CONTRAST',
      description:
        'Shallow depth of field creates excellent figure-ground separation, isolating the product and message from background visual noise.',
    },
    {
      name: 'SPATIAL PROXIMITY / DEPTH',
      description:
        "Pushing the sharply focused bottle into the viewer's near-space creates a strong sense of physical presence and premium quality.",
    },
  ],
  topImprovements: [
    {
      name: 'TEXT',
      description:
        'Move the text layer in front of the product imagery to guarantee instant legibility for fast-moving traffic.',
    },
    {
      name: 'GESTALT PRINCIPLES',
      description:
        'Bring the text layer to the foreground or shift the bottle slightly off-center to eliminate reliance on cognitive closure during a 2-second exposure.',
    },
    {
      name: 'SIZE',
      description:
        'Increase the scale of the top-left Luso logo by 30% to ensure faster brand linkage during brief exposures.',
    },
  ],
}
